'use client'

import { useEffect, useMemo, useRef, useState } from "react";
import { CraftAssetField } from "tsconfig/craft-types";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Glide from "@glidejs/glide";
import { throttle } from "lodash";
import { MArticleLink } from "../molecules/m-article-link";
import { AButton } from "../atoms/a-button";
import "./c-related-articles.css";
import { useSiteInfo } from "shared";
import { relatedArticlesQuery } from "../../helpers/related-articles";
import { withDefaultProps } from '../../helpers/with-default-props'

type Article = {
  id?: string;
  uri?: string;
  title?: string;
  typeHandle?: string;
  testimonialOccupation?: string;
  image?: Array<CraftAssetField>;
};

export type CRelatedArticlesBlock = {
  id?: string;
  typeHandle?: string;
  idName?: string;
  heading?: string;
  articles?: Array<Article>;
};

type Props = {
  field: Array<CRelatedArticlesBlock>;
  excludeId?: number | null;
  loadMoreLabel?: string;
  /** First extra articles (relatedArticlesQuery), fetched by the server */
  extraArticles?: Array<Article>;
  /** How many extra articles exist, to show "load more" */
  extraCount?: number;
  /** Endpoint that returns more articles as { articles } */
  articlesUrl?: string;
};

const defaultProps = {
  excludeId: null,
  loadMoreLabel: "More",
  articlesUrl: "/api/articles",
};

const CRelatedArticles = (inputProps: Props) => {
  const {
    field,
    excludeId,
    loadMoreLabel,
    extraArticles: initialExtraArticles,
    extraCount,
    articlesUrl,
  } = withDefaultProps(inputProps, defaultProps)
  const { t } = useTranslation("common");
  const siteInfo = useSiteInfo();

  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  const mobileBreakpoint = 768;

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const glideRef = useRef<Glide | null>(null);

  const content = useMemo(() => {
    return field && field.length ? field[0] : null;
  }, [field]);

  const extraQuery = useMemo(() => relatedArticlesQuery(field, excludeId), [field, excludeId]);

  // The first extra articles come from the server (extraArticles); more load from articlesUrl
  const [extraArticles, setExtraArticles] = useState<Array<Article>>(initialExtraArticles ?? []);

  useEffect(() => {
    setExtraArticles(initialExtraArticles ?? []);
  }, [initialExtraArticles]);

  const articles = useMemo(() => {
    return (
      content?.articles?.concat(extraArticles?.length ? extraArticles : []) || []
    );
  }, [content, extraArticles]);

  const getArticleTitle = (article: Article) => {
    if (article.typeHandle === "testimonialPages") {
      if (siteInfo.isJP) {
        return `${article.title}`;
      }
      // Occupation before the name; without an empty occupation ("null …") or one that repeats the title
      const occupation = article.testimonialOccupation?.trim();
      return occupation && occupation !== article.title?.trim()
        ? `${occupation} ${article.title}`
        : `${article.title}`;
    } else {
      return "";
    }
  };

  const getArticleSubtitle = (article: Article) => {
    if (article.typeHandle === "testimonialPages" && siteInfo.isJP) {
      return article.testimonialOccupation;
    }
  };

  const loadMore = async () => {
    const params = new URLSearchParams({
      kind: "related",
      site: siteInfo.handle,
      type: extraQuery.type,
      exclude: extraQuery.exclude.slice(1).join(","),
      offset: String(extraArticles.length),
      limit: "3",
    });
    const response = await fetch(`${articlesUrl}?${params}`);
    if (!response.ok) return;
    const { articles: more } = (await response.json()) as { articles: Array<Article> };
    setExtraArticles((previous) => [...previous, ...more]);
  };

  const goToSlide = (i: number) => {
    if (glideRef.current) {
      glideRef.current.go(`=${i}`);
    }
  };

  const handleWindowResize = () => {
    setWindowWidth(window.innerWidth);
    if (window.innerWidth < mobileBreakpoint && !glideRef.current) {
      mountSlider();
    } else if (window.innerWidth >= mobileBreakpoint && glideRef.current) {
      unmountSlider();
    }
  };

  const handleWindowResizeThrottled = throttle(handleWindowResize, 100);

  const mountSlider = () => {
    if (sliderRef.current) {
      const slider = new Glide(sliderRef.current, {
        direction: siteInfo.dir as "ltr" | "rtl" | undefined,
        gap: 20,
        rewind: false,
      });
      slider.mount();
      slider.on("run", () => {
        setActiveSlide(slider.index);
      });

      glideRef.current = slider;
    }
  };

  const unmountSlider = () => {
    if (glideRef.current) {
      glideRef.current.destroy();
      glideRef.current = null;
    }
  };

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    if (content) {
      window.addEventListener("resize", handleWindowResizeThrottled);
    }
    return () => {
      if (content) {
        window.removeEventListener("resize", handleWindowResizeThrottled);
      }
    };
  }, []);

  useEffect(() => {
    if (content?.articles?.length && window.innerWidth < mobileBreakpoint) {
      mountSlider();
    }

    return () => {
      if (content && glideRef.current) {
        glideRef.current.destroy();
        glideRef.current = null;
      }
    };
  }, [content, extraArticles]);

  if (!content?.articles?.length) {
    return <></>;
  }

  return (
    <section className="c-related-articles mt-26 mb-14 w-full">
      <div className="c-related-article-container o-container o-container--lg w-full">
        {content.heading && (
          <h2 className="text-h6 md:text-h4 font-light text-neutral-dark-gray mb-6 md:mb-8">
            {content.heading}
          </h2>
        )}
        <div ref={sliderRef} className="c-related-articles__slider">
          <div className="glide__track" data-glide-el="track">
            <div
              className={classNames("c-related-articles", {
                glide__slides: windowWidth <= mobileBreakpoint,
                "flex flex-wrap": windowWidth > mobileBreakpoint,
              })}
            >
              {articles.map((article: Article) => {
                return (
                  <MArticleLink
                    key={article.id}
                    className="c-related-articles__item glide__slide"
                    url={`/${article.uri}`}
                    heading={getArticleTitle(article) ?? ""}
                    subheading={getArticleSubtitle(article)}
                    image={article.image?.length ? article.image[0].url : null}
                    imageAlt={
                      article.image?.length ? article.image[0].title : null
                    }
                    linkText={t("read-story") ?? "Read Story"}
                  />
                );
              })}
            </div>
          </div>
        </div>
        {(extraArticles?.length ?? 0) < (extraCount ?? 0) && (
          <div className="hidden md:flex justify-center mt-12">
            <AButton
              type="button"
              style="outline"
              color="gray"
              size="lg"
              onClick={loadMore}
            >
              <span>{loadMoreLabel}</span>
            </AButton>
          </div>
        )}
        <div className="md:hidden flex justify-center mt-9">
          {articles.map((item, n) => {
            return (
              <button
                key={n}
                className="flex items-center justify-center w-6 h-6"
                onClick={() => goToSlide(n)}
              >
                <div
                  className={classNames(
                    "w-2 h-2 rounded-full transition-colors duration-200 ease-out",
                    {
                      "bg-primary-gray": activeSlide !== n,
                      "bg-primary-teal": activeSlide === n,
                    }
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

CRelatedArticles.defaultProps = defaultProps;

export { CRelatedArticles };

export { CRelatedArticlesFragmets } from '../../fragments/c-related-articles'
